import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card } from 'react-bootstrap';
import Highcharts from 'highcharts/highstock';
import HC_exporting from 'highcharts/modules/exporting';
import HC_accessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import { XRPSCAN_API_URL } from '../../config';
import { handleResponse } from '../common/Helpers';

const getTimeseriesData = (data) => {
    if (Array.isArray(data) && data?.length > 0) {
        return (
            data.map(d => {
                return {
                    x: new Date(d.date).getTime(),
                    y: (Array.isArray(d?.validators) ? d?.validators?.length : 0), 
                }
            })
            .sort((a, b) => { return a.x - b.x })
        );
    } else {
        return [];
    }
}

const getVoteChanges = (history) => {
    const voteChanges = [];
    for (let i = 0; i < history.length - 1; i++) {
        const current = history[i];
        const previous = history[i + 1];

        const currentValidators = new Set(current.validators);
        const previousValidators = new Set(previous.validators);

        const newVote = current.validators.filter(v => !previousValidators.has(v));
        const newVeto = previous.validators.filter(v => !currentValidators.has(v));

        voteChanges.push({ date: current.date, newVote, newVeto });
    }

    // Handle the last member of the array
    if (history.length > 0) {
        const lastMember = history[history.length - 1];
        if (lastMember?.date && lastMember?.validators) {
            voteChanges.push({
                date: lastMember.date,
                newVote: lastMember.validators,
                newVeto: []
            });
        }
    }

    return voteChanges;
}

const getVoteFlags = (history, validatorRegistry) => {
    const voteFlags = [];
    const voteChanges = getVoteChanges(history);
    for (const change of voteChanges) {
        // Add Yeas
        if (change?.newVote?.length > 0) {
            for (const vote of change?.newVote) {
                const voterName = validatorRegistry[vote] || vote;
                voteFlags.push({
                    x: new Date(change.date).getTime(),
                    title: "👍",
                    text: `${voterName} voted in favor`,
                    shape: 'circlepin',
                    color: Highcharts.getOptions().colors[2],
                })
            }
        }
        // Add Nays
        if (change?.newVeto?.length > 0) {
            for (const veto of change?.newVeto) {
                const voterName = validatorRegistry[veto] || veto;
                voteFlags.push({
                    x: new Date(change.date).getTime(),
                    title: "👎",
                    text: `${voterName} voted against`,
                    shape: 'squarepin',
                    color: Highcharts.getOptions().colors[7],
                })
            }
        }
    }
    return voteFlags.sort((a, b) =>  a.x - b.x );
}

const highchartsConfig = (amendment, voteData = [], validatorRegistry = {}) => {
    const timeseriesData = getTimeseriesData(voteData);
    const voteFlags = getVoteFlags(voteData, validatorRegistry)
    const chartColor = Highcharts.getOptions().colors[0];
    console.log(`timeseriesData`, timeseriesData);
    console.log(`voteFlags`, voteFlags)

    return {
        chart: {
            type: "areaspline",
            scrollablePlotArea: {
                minWidth: 700,
                scrollPositionX: 1
            },
            height: 450
        },
        xAxis: {
            type: 'datetime',
            minTickInterval: 30 * 24 * 36e5,
            labels: {
                align: 'left'
            }
        },
        title: {
            text: amendment?.name||'',
        },
        subtitle: {
            text: 'Amendment voting history',
        },
        caption: {
            text: `XRPL validators considered and voted on the ${amendment?.name} amendment over a span of ${voteData?.length} days. This amendment was introduced in rippled ${amendment?.introduced} and is ${amendment?.enabled ? 'enabled' : 'not yet enabled'} on the XRPL mainnet.`
        },
        credits: {
            enabled: false
        },
        accessibility: {
            enabled: true,
        },
        tooltip: {
            headerFormat: '<span style="font-size: 11px;color:#666">{point.x:%Y-%m-%d}</span><br>',
            style: {
                width: '250px'
            },
        },
        rangeSelector: {
            selected: 5,
            enabled: true,
        },
        navigator: {
            enabled: true,
        },
        yAxis: [
            {
                max: 40,
                title: {
                    text: 'Number of votes'
                },
                gridLineColor: 'rgba(0, 0, 0, 0.07)'
            }
        ],
        plotOptions: {
            series: {
                marker: {
                    enabled: false,
                    symbol: 'square',
                    radius: 2
                },
                fillOpacity: 0.5
            },
            flags: {
                tooltip: {
                    xDateFormat: '%B %e, %Y'
                },
                accessibility: {
                    point: {
                        valueDescriptionFormat:
                            '{xDescription}. {point.title}: {point.text}.'
                    }
                },
                opacity: 0.8
            }
        },
        series: [
            {
                yAxis: 0,
                name: 'Number of votes',
                id: 'votes',
                type: 'areaspline',
                step: 'left',
                color: chartColor,
                tooltip: {
                    pointFormat: '<br/><b>{point.y}</b><br/>',
                    valueSuffix: ' votes',
                    shared: true
                },
                fillColor: {
                    linearGradient: {
                        x1: 0,
                        y1: 0,
                        x2: 0,
                        y2: 1
                    },
                    stops: [
                        [0, chartColor],
                        [1, new Highcharts.Color(chartColor).setOpacity(0).get('rgba')]
                    ],
                },
                data: timeseriesData,
            },
            {
                type: 'flags',
                name: 'Voting events',
                data: voteFlags,
                onSeries: 'votes',
            }
        ]
    }
}

const AmendmentVoteHistory = (props) => {
    const { amendment } = props;
    const [ chartConfig, setChartConfig ] = useState(highchartsConfig(amendment, [], {}));
    HC_exporting(Highcharts);
    HC_accessibility(Highcharts);

    // Fetch Amendment voting history
    useEffect(() => {
        let isCancelled = false;

        const fetchData = async () => {
            const validators = {};
            let votingData = [];

            try {
                // Try to fetch validators first
                const validatorResponse = await fetch(`${XRPSCAN_API_URL}/validatorregistry/well-known`);
                const validatorData = await handleResponse(validatorResponse);

                for (const d of validatorData) {
                    if (d?.master_key && (d?.domain || d?.domain_legacy)) {
                        validators[d.master_key] = d.domain || d.domain_legacy || d.master_key;
                    }
                }
            } catch (error) {
                // Continue with empty validators if this fails
            }

            try {
                // Fetch voting history
                const votingResponse = await fetch(`${XRPSCAN_API_URL}/amendment/${amendment.amendment_id}/votinghistory`);
                votingData = await handleResponse(votingResponse);
            } catch (error) {
                // Use empty array if voting history fails
            }

            // Only update state if component is still mounted
            if (!isCancelled) {
                setChartConfig(highchartsConfig(amendment, votingData, validators));
            }
        };

        fetchData();

        return () => {
            isCancelled = true;
            setChartConfig({});
        }
    }, [amendment]);

    return (
        <Card className='shadow-sm'>
            <Card.Body>
                <HighchartsReact
                    highcharts={Highcharts}
                    constructorType={'stockChart'}
                    options={chartConfig}
                />
            </Card.Body>
        </Card>
    );
}

AmendmentVoteHistory.propTypes = {
    amendment: PropTypes.object.isRequired,
}

export default AmendmentVoteHistory;